import React, { ElementType, FC } from 'react';
import Link from 'next/link';
import cx from 'classnames';
import { PlusIcon } from '../Icons/PlusIcon';
import ProtectWithLogin from '../ProtectWithLogin/ProtectWithLogin';
import Tooltip from '../Tooltip/Tooltip';
import Typo from '../Typography/Typo';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';

export type EmptyStateButtonProp = 'post' | 'prompt';

type TTerritoryEmptyStateButton = {
  title: string;
  onClick?: () => void;
  href?: string;
  as?: ElementType;
  isAuthenticated?: boolean;
  canCreateInTerritory?: boolean;
  emptyStateType: EmptyStateButtonProp;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
};

export const TerritoryEmptyStateButton: FC<TTerritoryEmptyStateButton> = ({
  title,
  onClick,
  href,
  as: Component = 'button',
  isAuthenticated = false,
  canCreateInTerritory = false,
  emptyStateType,
  territorySlug,
  territoryVersion,
  ...rest
}) => {
  const isAnchor = Component === 'a';
  const anchorProps = isAnchor
    ? {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {};

  const isButtonDisabled = isAuthenticated && !canCreateInTerritory;

  const button = (
    <Component
      onClick={onClick}
      className={cx(
        'flex py-2 px-4 items-center justify-center border border-t2-neutral-grey03 rounded-2xl hover:bg-t2-neutral-grey03 cursor-pointer',
        {
          'text-t2-neutral-grey05 bg-t2-neutral-grey03 cursor-not-allowed pointer-events-none': isButtonDisabled,
        },
      )}
      disabled={isButtonDisabled}
      {...anchorProps}
      {...rest}
    >
      <PlusIcon
        className={cx('w-5 h-5 mr-2 stroke-t2-neutral-grey06', {
          '!stroke-t2-neutral-grey05': isButtonDisabled,
        })}
      />
      {title}
    </Component>
  );

  const wrappedButton = isAuthenticated ? button : <ProtectWithLogin>{button}</ProtectWithLogin>;

  return (
    <div>
      {isAuthenticated && !canCreateInTerritory ? (
        <Tooltip trigger={wrappedButton} direction="bottom" cursor="default">
          <Typo variant="p2" className="!text-sm whitespace-normal font-light">
            {territoryVersion === TerritoryVersionType.V2 ? (
              <>
                Claim a membership role with access to write new {emptyStateType} in this Territory. Explore available
                membership roles{' '}
                <Link href={`/t/${territorySlug}/join/`} className="underline">
                  here
                </Link>
                .
              </>
            ) : (
              <>Join the Territory to create a new {emptyStateType} </>
            )}
          </Typo>
        </Tooltip>
      ) : (
        wrappedButton
      )}
    </div>
  );
};
